import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Column from "@/atoms-react/column/Column.react";
import { Logo } from "@/components/logo/Logo.react";
import { useAuth } from "@/features/auth/hooks/use-auth-react";
import { jwtDecode } from "@/features/auth/invalid-token-error";
import { AccountWelcomeOutput } from "@/features/auth/types";
import { getJwt, setJwt } from "@/features/auth/utils";
import { useCguListDataQuery } from "@/features/cgu/hooks/use-get-cgu";
import { CGU_TYPE } from "@/features/cgu/types";
import { findMissingProfileKeysSignup } from "@/features/flow/constants.ts";
import { useManipulatePartner } from "@/features/flow/hooks/use-manipulate-partner";
import { Routes } from "@/features/router";
import { useUser } from "@/features/user/hooks/use-user";
import { useTaxAddress } from "@/features/user/tax_address/hooks/use-taxAdress";
import { UserType } from "@/features/user/types";
import { useLocale } from "@/features/utils/hooks/use-locale";
import type { SignupOutput } from "@/models/signup";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setInitialMissingFields } from "@/redux/slices/missingFieldsSlice";
import { setOlkypassLevel } from "@/redux/slices/olkypassSlice";
import { resetSignup, setMirrorUuid } from "@/redux/slices/signupSlice";
import { setExpirationToken, setUser } from "@/redux/slices/userSlice";
import { getRoute } from "@/utils/routes";
import { animatedNext } from "../layout/AnimatedForm.react";
import { DocumentsGateway, DocumentsGatewayAction } from "./documents/index.react";
import type { AuthenticateOutput } from "./login/authenticate/Authenticate.react";
import LoginRoot from "./login/index.react";
import CguMissing from "./signup/cgu";
import SignupRoot from "./signup/index.react";
import AccountWelcome from "./welcome/index.react";
import useConfirmMirrorUuidMutation from "@/features/flow/hooks/use-confirm-mirror";
import { privyCleaner } from "@/features/wallet/utils/privyClaner";

export type LoginInfo = {
  welcome: AccountWelcomeOutput | null;
  login: AuthenticateOutput | null;
  signup: SignupOutput | null;
};

export const Gateways = () => {
  const dispatch = useAppDispatch();
  const [isLoadingConnection, setIsLoadingConnection] = useState(false);
  const { t } = useTranslation(["pass"]);
  const { block, step, session_uuid } = useParams();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const { useCountries, useCgusavedQuery, useCguCheckSave } = useUser();
  const { refetch: refetchCgu } = useCgusavedQuery();
  const { locale } = useLocale();
  const { createAddressTaxMutation } = useTaxAddress();
  const { mutateAsync: createTaxAddres } = createAddressTaxMutation();
  const { data: countries } = useCountries(locale.language);
  const [searchParams] = useSearchParams();
  const [loadingCheck, setLoadingCheck] = useState<boolean>(false);
  const [loadingCheckMirror, setLoadingCheckMirror] = useState<boolean>(false);
  const mirorIdentity = searchParams.get("mirror_uuid");
  const emailSearchParams = searchParams.get("email");

  const profilSelector = useAppSelector((state) => state.user.user);
  const { mutateAsync: saveCgu } = useCguCheckSave();
  const { data: cguListData } = useCguListDataQuery();
  const { useUserProfile } = useUser();

  const { mutate: checkEmailExists } = useConfirmMirrorUuidMutation({
    onError: () => toast.error(t("flow.id_control.error.account_invalid"))
  });

  const { data: profile, refetch } = useUserProfile({
    enabled: false,
    queryKey: ["user", "profile"]
  });
  const getMyCountry = (alpha2: string) => {
    return (countries?.countries ?? [])
      .find((country) => country.alpha2.toLocaleLowerCase() == alpha2.toLocaleLowerCase())
      ?.id.toString();
  };
  const handleCheckMail = async (email: string, mirror_uuid: string) => {
    checkEmailExists(
      { email: email, mirror_uuid: mirror_uuid },
      {
        onSuccess: (response) => {
          //selon la réponse on redirige vers confirmation mail + missing fields (flow existant) ou saisie mdp avec email pré saisi
          if (response.success) {
            dispatch(setMirrorUuid(mirorIdentity));
            if (response.data?.isAccountComplete) {
              sessionStorage.setItem("username", email);
              navigate(getRoute(Routes.flow_block, { session_uuid, block: "login" }), {
                replace: true
              });
            } else {
              navigate(getRoute(Routes.flow_block, { session_uuid, block: "signup" }));
              setGatewaysInfo((i) => ({
                ...i,
                welcome: {
                  ...(i.welcome as AccountWelcomeOutput),
                  email: email
                }
              }));
            }
            setLoadingCheck(false);
            setLoadingCheckMirror(false);
            return;
          }
        }
      }
    );
  };
  const [gatewaysData, setGatewaysInfo] = useState<LoginInfo>({
    welcome: null,
    login: null,
    signup: null
  });
  const manipulatePartner = useManipulatePartner();

  const checkMissedMandatoryData = async (profileParam?: UserType) => {
    const profile = profileParam
      ? profileParam
      : profilSelector !== null
        ? profilSelector
        : (await refetch()).data;
    if (profile?.naturalPerson) {
      const missingKeys = findMissingProfileKeysSignup(profile.naturalPerson);
      const missingKeysObj = Object.keys(missingKeys);
      if (missingKeysObj.length > 0) {
        dispatch(setInitialMissingFields(missingKeysObj));
        if (
          missingKeysObj.includes("identity.nif") &&
          profile &&
          profile.naturalPerson.NIF !== null &&
          profile.naturalPerson.nif_country !== null
        ) {
          await createTaxAddres({
            taxAddress: [
              {
                country: getMyCountry(profile.naturalPerson.nif_country) ?? null,
                tin: profile.naturalPerson.NIF,
                tinCheck: true,
                tinExist: true,
                apiCheck: true
              }
            ]
          });
          delete missingKeys["identity.nif"];
          if (Object.keys(missingKeys).length > 0) {
            sessionStorage.setItem(
              "NewOlkyPayUser",
              JSON.stringify({ profile: profile, missingKeys: missingKeys })
            );
            // const first_step = "phone.phone" in missingKeys ? "phone" : "identity";
            const first_step = "identity";
            navigate(
              getRoute(Routes.flow_step, {
                session_uuid,
                block: "required_field",
                step: first_step
              })
            );
          } else await manipulatePartner(profile);
        } else {
          sessionStorage.setItem(
            "NewOlkyPayUser",
            JSON.stringify({ profile: profile, missingKeys: missingKeys })
          );
          const first_step = "phone.phone" in missingKeys ? "phone" : "identity";
          navigate(
            getRoute(Routes.flow_step, {
              session_uuid,
              block: "required_field",
              step: first_step
            })
          );
        }
      } else await manipulatePartner(profile);
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem("NewOlkyPayUser")) sessionStorage.removeItem("NewOlkyPayUser");
    if (mirorIdentity && emailSearchParams && block === "welcome") {
      setLoadingCheckMirror(true);

      handleCheckMail(emailSearchParams, mirorIdentity).finally(() => {
        setTimeout(() => {
          setLoadingCheckMirror(false);
        }, 1000);
      });
    }
  }, [mirorIdentity, emailSearchParams]);
  useEffect(() => {
    if (block === "login" && step === "reset") {
      return;
    }
    if (
      (gatewaysData[block || ""] !== undefined || !session_uuid || !gatewaysData.welcome?.email) &&
      !getJwt().token &&
      !(mirorIdentity && emailSearchParams)
    ) {
      // Redirect to welcome if invalid block or just reloaded the page now
      dispatch(resetSignup());
      if (sessionStorage.getItem("temp_uuid_real")) sessionStorage.removeItem("temp_uuid_real");
      navigate(getRoute(Routes.flow_block, { session_uuid, block: "welcome" }), {
        replace: true
      });
      privyCleaner();
      return;
    }
  }, []);
  if (loadingCheckMirror)
    return (
      <Column>
        <div className="flex flex-row justify-center">
          <div>
            <div className="flex h-full grow flex-col items-center justify-center">
              <Logo loading init className="animated mb-8 w-24" />
            </div>
          </div>
        </div>
      </Column>
    );
  return (
    <>
      {block === "documents" && (
        <DocumentsGateway
          action={DocumentsGatewayAction.KYCDocument}
          onValidated={() => refresh(true)}
        />
      )}

      {block === "signup" && (
        <SignupRoot
          cguListData={cguListData ?? []}
          input={{ email: gatewaysData.welcome?.email || "" }}
        />
      )}
      {block === "cgu" && (
        <CguMissing
          loadingCheck={loadingCheck}
          onValidated={async (output) => {
            setLoadingCheck(true);
            await saveCgu({ TermUuid: output.UUID });
            await checkMissedMandatoryData();
          }}
          cguList={cguListData ?? []}
        />
      )}
      {block === "login" && (
        <LoginRoot
          input={{
            email: emailSearchParams || gatewaysData.welcome?.email || "",
            is2faActivated: gatewaysData.welcome?.is2faActivated || false
          }}
          isLoading={isLoadingConnection}
          onValidated={async (jwt) => {
            setIsLoadingConnection(true);
            setJwt(jwt);
            if (jwt.token) {
              const { exp } = jwtDecode(jwt.token);
              dispatch(setExpirationToken(Number(exp)));
            }
            await refetch();
            if (profile) {
              dispatch(setOlkypassLevel(profile.olkyPassLevel.olkypass_status.level));
              dispatch(setUser(profile));
            }
            const { data: cgu } = await refetchCgu();
            if (cgu) {
              const CguChecked = cgu.find((cgu) => cgu.TermType === CGU_TYPE.REGISTER);
              if (!CguChecked) {
                navigate(getRoute(Routes.flow_block, { session_uuid, block: "cgu" }));
              } else await checkMissedMandatoryData(profile);
            }
            setIsLoadingConnection(false);
          }}
        />
      )}
      {block === "welcome" && (
        <AccountWelcome
          onValidated={(welcome) => {
            const { emailExists, existStatus } = welcome;
            animatedNext("forward", async () => {
              setGatewaysInfo((i) => ({ ...i, welcome }));
              if ((emailExists || existStatus) && !welcome.realExist && !welcome.mirrorExist) {
                navigate(getRoute(Routes.flow_block, { session_uuid, block: "login" }));
              } else if (
                welcome.existAsAlias &&
                ((!welcome.mirrorExist && !welcome.realExist && welcome.real_uuid) ||
                  (welcome.mirrorExist && !welcome.realExist && !welcome.real_uuid) ||
                  (!welcome.mirrorExist && welcome.realExist && !welcome.real_uuid))
              ) {
                toast.error(t("olkypass.general.mail_already_exist"));
              } else {
                if (welcome.realExist && welcome.real_uuid) {
                  sessionStorage.setItem("temp_uuid_real", welcome.real_uuid);
                }
                navigate(
                  getRoute(Routes.flow_step, {
                    session_uuid,
                    block: "signup",
                    step: "verify_email"
                  }),
                  {
                    replace: true
                  }
                );
              }
            });
          }}
        />
      )}
      {block === "required_field" && (
        <SignupRoot
          input={{ email: gatewaysData.welcome?.email || "" }}
          cguListData={cguListData ?? []}
        />
      )}
    </>
  );
};
